import React from "react"
import { useRouter } from "next/router"

// components
import { Drawer, Steps } from "antd"

// configs
import config from "config"

// firebase
import { Auth } from "services/firebase"
import { signInAnonymously } from "firebase/auth"

// context
import { useTranslation } from "react-i18next"
import { useOrders } from "store/orders"
import { useFunnel } from "store/funnel"

// compeonnts
import OrderDrawerOrder from "components/OrderDrawerOrder"
import OrderDrawerInfos from "components/OrderDrawerInfos"
import OrderDrawerConfirmation from "components/OrderDrawerConfirmation"
import OrderDrawerFinish from "components/OrderDrawerFinish"

const OrderDrawer = ({ open, onClose }) => {
  const { t } = useTranslation()
  const funnel = useFunnel()
  const router = useRouter()

  const orders = useOrders()
  const [currentOrder, setCurrentOrder] = React.useState()
  const [orderStep, setOrderStep] = React.useState(0)

  const tableNumber = router.query.table // e.g. "10"
  const tableMode = !!tableNumber && !!config.eat_in

  React.useEffect(() => {
    if (document.querySelectorAll(".order-drawer .ant-drawer-body").length > 0)
      document.querySelectorAll(".order-drawer .ant-drawer-body")[0].scrollTo({
        top: 0,
        behavior: "smooth",
      })
  }, [orderStep])

  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC__APP_ENV === "development")
      console.log("CurrentOrder: ", currentOrder)
  }, [currentOrder])

  const stepOrder = (
    <OrderDrawerOrder
      onFinish={async () => {
        if (tableMode) {
          try {
            if (!Auth.currentUser) {
              await signInAnonymously(Auth)
            }
          } catch (e) {
            console.error("Anon sign-in failed:", e)
          }
          const tableOrder = {
            resto_key: config.key,
            type: "eat-in",
            table: tableNumber,
            items: orders.order.list,
            total: orders.order.total,
            firstname: null,
            familyname: null,
            tel: null,
          }
          setCurrentOrder(tableOrder)
          setOrderStep(1)
          funnel.add("order_reviewed_table")
        } else {
          setOrderStep(1)
          funnel.add("order_reviewed")
        }
      }}
    />
  )

  const stepInfos = (
    <OrderDrawerInfos
      back={() => {
        setOrderStep(0)
        setCurrentOrder(null)
        funnel.add("order_back")
      }}
      onFinish={(order) => {
        setOrderStep(2)
        setCurrentOrder(order)
        funnel.add("order_infos")
      }}
    />
  )

  const stepConfirmation = (backStep) => (
    <OrderDrawerConfirmation
      order={currentOrder}
      back={() => {
        setOrderStep(backStep)
        setCurrentOrder(null)
        funnel.add("order_back")
      }}
      onFinish={(orderId) => {
        setOrderStep(backStep + 2)
        funnel.add("order_sent")
        setCurrentOrder((prev) => ({ ...prev, id: orderId }))
      }}
    />
  )

  const stepFinish = (
    <OrderDrawerFinish
      orderId={currentOrder?.id}
      onFinish={() => {
        funnel.add("order_finished")
        setOrderStep(0)
        setCurrentOrder(null)
        orders.order.clear()
        onClose(currentOrder?.id)
      }}
    />
  )

  const steps = tableMode
    ? [
        { title: t("order"), content: stepOrder },
        { title: t("send_tab"), content: stepConfirmation(0) },
        { title: t("finish"), content: stepFinish },
      ]
    : [
        { title: t("order"), content: stepOrder },
        { title: t("infos_tab"), content: stepInfos },
        { title: t("send_tab"), content: stepConfirmation(1) },
        { title: t("finish"), content: stepFinish },
      ]

  return (
    <Drawer
      className="order-drawer"
      width={600}
      title={
        <Steps
          size="small"
          responsive={false}
          current={orderStep}
          type="navigation"
        >
          {steps.map((item) => (
            <Steps.Step key={item.title} title={item.title} icon={item.icon} />
          ))}
        </Steps>
      }
      placement="right"
      onClose={() => {
        const lastStep = steps.length - 1
        if (orderStep === lastStep) {
          orders.order.clear()
        }

        setCurrentOrder(null)
        setOrderStep(0)
        onClose(currentOrder?.id)
      }}
      open={open}
    >
      <div className="steps-content">{steps[orderStep].content}</div>
    </Drawer>
  )
}

export default OrderDrawer
